// Timeline math & helpers

export const ZOOM_LEVELS = {
  day: { key: "day", label: "Day", pxPerDay: 34, headerUnit: "day" },
  week: { key: "week", label: "Week", pxPerDay: 12, headerUnit: "week" },
  month: { key: "month", label: "Month", pxPerDay: 3.6, headerUnit: "month" },
  quarter: { key: "quarter", label: "Quarter", pxPerDay: 1.4, headerUnit: "quarter" },
  year: { key: "year", label: "Year", pxPerDay: 0.55, headerUnit: "year" },
};

export function parseDate(str) {
  if (!str) return null;
  if (str instanceof Date) return str;
  // Accept YYYY-MM-DD or ISO
  const d = new Date(str);
  if (isNaN(d.getTime())) return null;
  // Zero-out time for simpler math
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

export function formatDate(d) {
  if (!d) return "";
  const dt = parseDate(d);
  if (!dt) return "";
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const day = String(dt.getUTCDate()).padStart(2, "0");
  return `${dt.getUTCFullYear()}-${m}-${day}`;
}

export function prettyDate(d) {
  if (!d) return "—";
  const dt = parseDate(d);
  if (!dt) return "—";
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

export function daysBetween(a, b) {
  if (!a || !b) return 0;
  const da = parseDate(a);
  const db = parseDate(b);
  if (!da || !db) return 0;
  return Math.round((db - da) / 86400000);
}

export function addDays(d, n) {
  const dt = parseDate(d);
  if (!dt) return null;
  const nd = new Date(dt);
  nd.setUTCDate(nd.getUTCDate() + n);
  return nd;
}

// Compute the visible range from project dates + task dates (+ padding)
export function computeRange(project, tasks) {
  const all = [];
  if (project?.start_date) all.push(parseDate(project.start_date));
  if (project?.go_live_date) all.push(parseDate(project.go_live_date));
  for (const t of tasks || []) {
    for (const v of Object.values(t.dates || {})) {
      const d = parseDate(v);
      if (d) all.push(d);
    }
  }
  if (all.length === 0) {
    const today = new Date();
    const start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1));
    const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 3, 1));
    return { start, end };
  }
  let min = all[0];
  let max = all[0];
  for (const d of all) {
    if (d < min) min = d;
    if (d > max) max = d;
  }
  // Add padding (2 weeks on each side)
  const start = addDays(min, -14);
  const end = addDays(max, 14);
  return { start, end };
}

export function dateToX(d, rangeStart, pxPerDay) {
  const dt = parseDate(d);
  if (!dt) return null;
  const days = (dt - rangeStart) / 86400000;
  return days * pxPerDay;
}

// Inverse of dateToX — converts an x position (in pixels) into a YYYY-MM-DD date string
// snapped to the day. Returns null if pxPerDay is invalid.
export function xToDate(x, rangeStart, pxPerDay) {
  if (!pxPerDay || pxPerDay <= 0) return null;
  const days = Math.floor(x / pxPerDay);
  const dt = new Date(rangeStart.getTime() + days * 86400000);
  return formatDate(dt);
}

// Build timeline header units
export function buildHeaderUnits(range, zoomKey) {
  const { start, end } = range;
  const units = [];
  if (zoomKey === "day" || zoomKey === "week") {
    // Top band = months, bottom band = days (day zoom) or week labels (week zoom)
    let cur = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
    while (cur <= end) {
      const next = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth() + 1, 1));
      const clampStart = cur < start ? start : cur;
      const clampEnd = next > end ? end : next;
      units.push({
        level: "top",
        label: cur.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" }),
        start: clampStart,
        end: clampEnd,
      });
      cur = next;
    }
    // Bottom band
    if (zoomKey === "day") {
      let d = new Date(start);
      while (d <= end) {
        units.push({
          level: "bottom",
          label: d.getUTCDate(),
          sub: ["S", "M", "T", "W", "T", "F", "S"][d.getUTCDay()],
          start: new Date(d),
          end: addDays(d, 1),
          isWeekend: d.getUTCDay() === 0 || d.getUTCDay() === 6,
        });
        d = addDays(d, 1);
      }
    } else {
      // Weeks — snap to Monday
      let d = new Date(start);
      const dow = d.getUTCDay();
      const offset = (dow + 6) % 7; // days since Monday
      d = addDays(d, -offset);
      while (d <= end) {
        const weekEnd = addDays(d, 7);
        units.push({
          level: "bottom",
          label: `W${getWeekNumber(d)}`,
          sub: d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }),
          start: new Date(d),
          end: weekEnd,
        });
        d = weekEnd;
      }
    }
  } else if (zoomKey === "month") {
    // Top: year, bottom: month
    let y = start.getUTCFullYear();
    while (y <= end.getUTCFullYear()) {
      units.push({
        level: "top",
        label: String(y),
        start: new Date(Date.UTC(y, 0, 1)),
        end: new Date(Date.UTC(y + 1, 0, 1)),
      });
      y++;
    }
    let cur = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
    while (cur <= end) {
      const next = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth() + 1, 1));
      units.push({
        level: "bottom",
        label: cur.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" }),
        sub: null,
        start: new Date(cur),
        end: next,
      });
      cur = next;
    }
  } else if (zoomKey === "quarter") {
    let y = start.getUTCFullYear();
    while (y <= end.getUTCFullYear()) {
      units.push({
        level: "top",
        label: String(y),
        start: new Date(Date.UTC(y, 0, 1)),
        end: new Date(Date.UTC(y + 1, 0, 1)),
      });
      for (let q = 0; q < 4; q++) {
        units.push({
          level: "bottom",
          label: `Q${q + 1}`,
          sub: String(y),
          start: new Date(Date.UTC(y, q * 3, 1)),
          end: new Date(Date.UTC(y, q * 3 + 3, 1)),
        });
      }
      y++;
    }
  } else if (zoomKey === "year") {
    // top: decade, bottom: year
    const startY = Math.floor(start.getUTCFullYear() / 10) * 10;
    const endY = Math.ceil((end.getUTCFullYear() + 1) / 10) * 10;
    for (let d = startY; d < endY; d += 10) {
      units.push({
        level: "top",
        label: `${d}s`,
        start: new Date(Date.UTC(d, 0, 1)),
        end: new Date(Date.UTC(d + 10, 0, 1)),
      });
    }
    for (let y = startY; y < endY; y++) {
      units.push({
        level: "bottom",
        label: String(y),
        start: new Date(Date.UTC(y, 0, 1)),
        end: new Date(Date.UTC(y + 1, 0, 1)),
      });
    }
  }
  return units;
}

function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

export function totalWidth(range, pxPerDay) {
  const days = Math.max(1, daysBetween(range.start, range.end));
  return Math.max(400, days * pxPerDay);
}

export const WORKSTREAM_PALETTE = [
  { bg: "bg-blue-100", text: "text-blue-800", dot: "bg-blue-500" },
  { bg: "bg-purple-100", text: "text-purple-800", dot: "bg-purple-500" },
  { bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-500" },
  { bg: "bg-orange-100", text: "text-orange-800", dot: "bg-orange-500" },
  { bg: "bg-pink-100", text: "text-pink-800", dot: "bg-pink-500" },
  { bg: "bg-cyan-100", text: "text-cyan-800", dot: "bg-cyan-500" },
  { bg: "bg-amber-100", text: "text-amber-800", dot: "bg-amber-500" },
  { bg: "bg-rose-100", text: "text-rose-800", dot: "bg-rose-500" },
];

export function workstreamColor(name, overrides) {
  if (!name) return { bg: "bg-slate-100", text: "text-slate-600", dot: "bg-slate-400" };
  // If the project has an explicit hex for this workstream, synthesise a matching palette.
  const hex = overrides && overrides[name];
  if (hex) {
    return {
      bg: "",
      text: "",
      dot: "",
      overrideHex: hex,
    };
  }
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return WORKSTREAM_PALETTE[hash % WORKSTREAM_PALETTE.length];
}

// Locked status groups; the statuses inside each group are editable per project.
export const STATUS_GROUPS = [
  { key: "not_started", label: "Not Started" },
  { key: "in_progress", label: "In Progress" },
  { key: "done", label: "Done" },
];

export const DEFAULT_STATUSES = [
  { id: "on_hold", label: "On Hold", group: "not_started", color: "#a78bfa", blocker: false },
  { id: "upcoming", label: "Upcoming", group: "not_started", color: "#64748b", blocker: false },
  { id: "to_do", label: "To Do", group: "not_started", color: "#94a3b8", blocker: false },
  { id: "in_progress", label: "In Progress", group: "in_progress", color: "#3b82f6", blocker: false },
  { id: "developing", label: "Developing", group: "in_progress", color: "#6366f1", blocker: false },
  { id: "researching", label: "Researching", group: "in_progress", color: "#06b6d4", blocker: false },
  { id: "blocked", label: "Blocked", group: "in_progress", color: "#ef4444", blocker: true },
  { id: "at_risk", label: "At Risk", group: "in_progress", color: "#f59e0b", blocker: true },
  { id: "completed", label: "Completed", group: "done", color: "#10b981", blocker: false },
  { id: "done", label: "Done", group: "done", color: "#22c55e", blocker: false },
  { id: "rejected", label: "Rejected", group: "done", color: "#78716c", blocker: false },
];

export const getStatuses = (project) =>
  Array.isArray(project?.statuses) ? project.statuses : DEFAULT_STATUSES;

export const findStatus = (statuses, label) => statuses.find((s) => s.label === label) || null;

export const isDoneStatus = (statuses, label) => findStatus(statuses, label)?.group === "done";

export const firstStatusIn = (statuses, group, preferred) => {
  const inGroup = statuses.filter((s) => s.group === group);
  return (inGroup.find((s) => s.label === preferred) || inGroup[0])?.label || "";
};

export const statusPillStyle = (statuses, label) => {
  const c = findStatus(statuses, label)?.color || "#94a3b8";
  return {
    backgroundColor: `${c}1a`,
    borderColor: `${c}55`,
    color: `color-mix(in srgb, ${c} 70%, #0f172a)`,
  };
};
